import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import {
  Pencil, Award, Crown, Gem, User, Building2,
  ShieldCheck, TrendingUp, ChevronRight
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import ReactCountryFlag from 'react-country-flag';
import { getIsoFromDialCode } from '@/utils/countryCodes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ── Category Config ───────────────────────────────────────────────────── */
const CATEGORY_CONFIG = {
  bronze:  { label: 'Bronze',  cls: 'bg-amber-100 text-amber-700 border-amber-300',    Icon: Award  },
  silver:  { label: 'Silver',  cls: 'bg-slate-100 text-slate-500 border-slate-300',    Icon: Award  },
  gold:    { label: 'Gold',    cls: 'bg-yellow-100 text-yellow-600 border-yellow-300', Icon: Crown  },
  diamond: { label: 'Diamond', cls: 'bg-cyan-100 text-cyan-600 border-cyan-300',       Icon: Gem    },
};

const NEXT_TIER = {
  bronze:  { next: 'silver',  clients: 10, cps: 2  },
  silver:  { next: 'gold',    clients: 30, cps: 5  },
  gold:    { next: 'diamond', clients: 50, cps: 10  },
  diamond: null,
};

const CategoryBadge = ({ category }) => {
  const c = CATEGORY_CONFIG[category?.toLowerCase()] || CATEGORY_CONFIG.bronze;
  const { Icon } = c;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {c.label}
    </span>
  );
};

const KYCBadge = () => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-green-100 text-green-700 border-green-200">
    <ShieldCheck className="h-3 w-3" /> Approved
  </span>
);

/* ── Row Components ─────────────────────────────────────────────────────── */
const ReadRow = ({ label, value, mono }) => (
  <div className="flex items-center py-3 gap-6 border-b border-slate-50 last:border-0">
    <span className="w-44 flex-shrink-0 text-sm font-medium text-slate-500">{label}</span>
    <span className={`text-sm text-slate-800 ${mono ? 'font-mono text-xs' : ''}`}>{value || '—'}</span>
  </div>
);

const DataRow = ({ label, value, mono }) => (
  <div className="flex items-center py-3 gap-6 border-b border-slate-50 last:border-0">
    <span className="w-44 flex-shrink-0 text-sm font-medium text-slate-500">{label}</span>
    <span className={`text-sm text-slate-800 ${mono ? 'font-mono text-xs' : ''}`}>{value || '—'}</span>
  </div>
);

const BadgeRow = ({ label, children }) => (
  <div className="flex items-center py-3 gap-6 border-b border-slate-50 last:border-0">
    <span className="w-44 flex-shrink-0 text-sm font-medium text-slate-500">{label}</span>
    {children}
  </div>
);

const SectionHeading = ({ label }) => (
  <div className="py-3 border-b border-slate-50">
    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</span>
  </div>
);

const ToggleRow = ({ label, checked, onChange, hint, testId }) => (
  <div className="flex items-center py-3 gap-6 border-b border-slate-50 last:border-0">
    <div className="w-44 flex-shrink-0">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
    </div>
    <button
      onClick={onChange}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-blue-600' : 'bg-slate-200'}`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform shadow ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
    <span className="text-sm text-slate-600">{checked ? 'Enabled' : 'Disabled'}</span>
  </div>
);

/* ── Main Component ─────────────────────────────────────────────────────── */
export default function PartnerDashboardProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'reseller';

  const [account, setAccount] = useState({});
  const [profile, setProfile] = useState({});
  const [counts, setCounts] = useState({ direct_clients: 0, direct_channel_partners: 0 });
  const [loading, setLoading] = useState(true);
  const [togglingMfa, setTogglingMfa] = useState(false);
  const [togglingConsent, setTogglingConsent] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [accRes, profRes, countRes] = await Promise.all([
          axios.get(`${API}/partner/account`),
          axios.get(`${API}/partner/profile`),
          axios.get(`${API}/partner/portal/counts`).catch(() => ({ data: { direct_clients: 0, direct_channel_partners: 0 } })),
        ]);
        setAccount(accRes.data);
        setProfile(profRes.data);
        setCounts(countRes.data);
      } catch {
        toast.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const toggleMfa = async () => {
    if (togglingMfa) return;
    setTogglingMfa(true);
    try {
      const newVal = !account.mfa_enabled;
      await axios.put(`${API}/partner/account`, { mfa_enabled: newVal });
      setAccount(a => ({ ...a, mfa_enabled: newVal }));
      toast.success(`MFA ${newVal ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update MFA');
    } finally {
      setTogglingMfa(false);
    }
  };

  const toggleWalletConsent = async () => {
    if (togglingConsent) return;
    setTogglingConsent(true);
    try {
      const newVal = !account.wallet_payment_consent;
      await axios.put(`${API}/partner/wallet-consent`, { wallet_payment_consent: newVal });
      setAccount(a => ({ ...a, wallet_payment_consent: newVal }));
      toast.success(`Wallet payment consent ${newVal ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update consent');
    } finally {
      setTogglingConsent(false);
    }
  };

  const currentCat = (account.account_category || 'bronze').toLowerCase();
  const nextTier = NEXT_TIER[currentCat];

  const roleLabel = profile.role === 'reseller' ? 'Synergy Partner'
    : profile.role === 'sub_reseller' ? 'Channel Partner'
    : profile.role || '';

  if (loading) return (
    <DashboardLayout role={role}>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout role={role}>
      <div className="max-w-3xl mx-auto space-y-2" data-testid="partner-dashboard-profile">

        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Profile</h1>
          <p className="text-sm text-slate-500 mt-1">Your complete account and personal information</p>
        </div>

        {/* ── User Profile Section ─────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="profile-section-user">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-blue-600" />
              <div>
                <h2 className="font-semibold text-slate-800">User Profile</h2>
                <p className="text-xs text-slate-500 mt-0.5">Your personal information</p>
              </div>
            </div>
            <Button
              size="sm" variant="outline"
              onClick={() => navigate('/partner/edit-profile?from=dashboard')}
              className="text-blue-600 border-blue-200 hover:bg-blue-600 hover:border-blue-600 flex items-center gap-2"
              data-testid="edit-profile-btn"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit Profile
            </Button>
          </div>
          <div className="px-6 divide-y divide-slate-50">
            <DataRow label="Full Name" value={profile.full_name} />
            <ReadRow label="Email" value={profile.email} />
            <div className="flex items-center py-3.5 gap-6">
              <span className="w-44 flex-shrink-0 text-sm font-medium text-slate-500">Mobile</span>
              <span className="text-sm text-slate-800 flex items-center gap-2">
                {profile.mobile_country_code && getIsoFromDialCode(profile.mobile_country_code) && (
                  <ReactCountryFlag
                    countryCode={getIsoFromDialCode(profile.mobile_country_code)}
                    svg style={{ width: '1.2em', height: '1.2em', borderRadius: '2px' }}
                  />
                )}
                {`${profile.mobile_country_code || ''} ${profile.mobile || ''}`.trim() || '—'}
              </span>
            </div>
            <ReadRow label="Role" value={roleLabel} />
          </div>
        </div>

        {/* ── Account Details Section ──────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="profile-section-account">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              <div>
                <h2 className="font-semibold text-slate-800">Account Details</h2>
                <p className="text-xs text-slate-500 mt-0.5">Business and account information</p>
              </div>
            </div>
            <Button
              size="sm" variant="outline"
              onClick={() => navigate('/partner/edit-account?from=dashboard')}
              className="text-blue-600 border-blue-200 hover:bg-blue-50 flex items-center gap-2"
              data-testid="edit-account-btn"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit Account Details
            </Button>
          </div>
          <div className="px-6">
            <SectionHeading label="General" />
            <DataRow label="Account Name" value={account.account_name} />
            <DataRow label="Account Type" value={account.account_type} />
            {account.account_id && <ReadRow label="Account ID" value={account.account_id} mono />}
            <ReadRow label="CRM ID" value={account.crm_id} mono />
            <ReadRow label="Account Status" value={account.account_status ? account.account_status.charAt(0).toUpperCase() + account.account_status.slice(1) : '—'} />
            {user?.role !== 'sub_reseller' && (
              <BadgeRow label="Account Category">
                <CategoryBadge category={currentCat} />
              </BadgeRow>
            )}
            <BadgeRow label="KYC Status">
              <KYCBadge />
            </BadgeRow>

            <SectionHeading label="Industry" />
            <DataRow label="Industry Group" value={account.industry_group} />
            <DataRow label="Sub-Vertical" value={account.sub_vertical} />
            <DataRow label="GST Number" value={account.gst_number} mono />
            <DataRow label="Website" value={account.website} />

            <SectionHeading label="Tax & Regulatory" />
            <DataRow
              label="PAN Number"
              value={account.pan ? '****' + account.pan.slice(-4) : '—'}
              mono
            />
            <DataRow label="TAN Number" value={account.tan} mono />
            <DataRow label="DUNS Number" value={account.duns} mono />

            <SectionHeading label="Settings" />
            <ToggleRow
              label="MFA"
              hint="Two-factor authentication"
              checked={!!account.mfa_enabled}
              onChange={toggleMfa}
              testId="dashboard-mfa-toggle"
            />
            {user?.role !== 'sub_reseller' && (
              <ToggleRow
                label="Wallet Payment Consent"
                hint="Auto-deduct from wallet for orders"
                checked={account.wallet_payment_consent !== false}
                onChange={toggleWalletConsent}
                testId="dashboard-wallet-consent-toggle"
              />
            )}
          </div>
        </div>

        {/* ── Category Progression — hidden for Channel Partners ────────── */}
        {user?.role !== 'sub_reseller' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 !mt-2" data-testid="profile-section-category">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-slate-800">Account Category Progression</h3>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <CategoryBadge category={currentCat} />
            {nextTier && (
              <>
                <ChevronRight className="h-4 w-4 text-slate-300" />
                <CategoryBadge category={nextTier.next} />
              </>
            )}
          </div>
          {nextTier ? (
            <div className="space-y-3">
              <p className="text-xs text-slate-600">
                To reach <strong className="capitalize">{nextTier.next}</strong>, you need:
              </p>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-slate-500">Direct Clients</span>
                  <span className="text-xs font-medium text-slate-700">{counts.direct_clients} / {nextTier.clients}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (counts.direct_clients / nextTier.clients) * 100)}%` }} />
                </div>
              </div>
              <p className="text-xs text-slate-400 text-center">— OR —</p>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-slate-500">Channel Partners</span>
                  <span className="text-xs font-medium text-slate-700">{counts.direct_channel_partners} / {nextTier.cps}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (counts.direct_channel_partners / nextTier.cps) * 100)}%` }} />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">Tiers: Bronze → Silver (10 clients) → Gold (30 clients) → Diamond (50 clients)</p>
            </div>
          ) : (
            <p className="text-sm text-cyan-700 font-medium">You've reached the highest tier!</p>
          )}
        </div>
        )}

      </div>
    </DashboardLayout>
  );
}
